/*! Sticky Add to cart (mobile) – theme-native */
import { formatMoney } from "utils";
import Cart from "cart-store";

const MOBILE_BREAKPOINT = "max-width: 47.9275em";

function initStickyAtc() {
  const section = document.querySelector(
    "section[data-sticky-atc-mobile='true']"
  );
  if (!section) return;

  const bar = section.querySelector(".sticky-atc");
  const formId = section.dataset.productFormId;
  const sectionId = section.dataset.sectionId;
  const stickyShow = section.dataset.stickyShow || "when_hidden";
  const showOnMobile = section.dataset.stickyAtcMobile === "true";
  const showOnDesktop = section.dataset.stickyAtcDesktop === "true";
  const hasVariants = bar?.dataset.hasVariants === "true";

  if (!bar || !formId) return;

  const form = document.getElementById(formId);
  const formBox = section.querySelector(".product-single__box");
  const submitBtn = bar.querySelector("[data-sticky-atc-submit]");
  const btnText = bar.querySelector("staged-action-text");
  const stagedAction = bar.querySelector("staged-action");
  const priceEl = bar.querySelector(".sticky-atc__price");
  const imgEl = bar.querySelector(".sticky-atc__img");
  const expandable = bar.querySelector(".sticky-atc__expandable");
  const variantsContainer = bar.querySelector("[data-sticky-atc-variants]");
  const closeBtn = bar.querySelector("[data-action-info-close]");

  let variantImages = {};
  const scriptEl = document.getElementById(
    `StickyAtcVariantImages-${sectionId}`
  );
  if (scriptEl) {
    try {
      const data = JSON.parse(scriptEl.textContent);
      variantImages = data.variantImages || {};
    } catch (_) {}
  }

  function setBarHeight(value) {
    document.documentElement.style.setProperty(
      "--stickyATC-height",
      value || "0px"
    );
  }

  function showBar() {
    bar.classList.add("is--shown");
    bar.setAttribute("aria-hidden", "false");
    if (!mobileQuery.matches && hasVariants) {
      buildVariantSelects();
      if (expandable) expandable.hidden = false;
    }
    requestAnimationFrame(() => setBarHeight(bar.offsetHeight + "px"));
  }

  function hideBar() {
    bar.classList.remove("is--shown");
    bar.setAttribute("aria-hidden", "true");
    setBarHeight("0px");
  }

  function updateFromVariant(variant) {
    if (!variant) return;
    if (imgEl && variantImages[variant.id]) {
      imgEl.src = variantImages[variant.id];
    }
    if (priceEl) {
      const saleClass =
        variant.compare_at_price > variant.price ? " price__number--sale" : "";
      priceEl.innerHTML = `
        <span class="price__number${saleClass}">
          <span class="money">${formatMoney(
            variant.price,
            window.theme.money_product_price_format
          )}</span>
        </span>
        ${
          variant.compare_at_price > variant.price
            ? `<s class="price__compare"><span class="money">${formatMoney(
                variant.compare_at_price,
                window.theme.money_format
              )}</span></s>`
            : ""
        }
      `;
    }
    if (submitBtn && btnText) {
      if (variant.available) {
        submitBtn.removeAttribute("disabled");
        btnText.textContent = window.theme.localize("ADD_TO_CART");
      } else {
        submitBtn.setAttribute("disabled", "disabled");
        btnText.textContent = window.theme.localize("SOLD_OUT");
      }
    }
  }

  function refreshBarHeight() {
    requestAnimationFrame(() => {
      setBarHeight(bar.offsetHeight + "px");
    });
  }

  function expandMinimal() {
    const productRow = bar.querySelector('.sticky-atc__product-row');
    /* Step 1: bring both elements into the flex flow at their starting state */
    if (productRow) productRow.style.display = 'flex';
    if (expandable) expandable.hidden = false; // removes [hidden] → display:flex at max-height:0
    /* Step 2: double rAF ensures display changes are painted before transitions fire */
    requestAnimationFrame(() => requestAnimationFrame(() => {
      bar.classList.add("is--minimal-expanded");
      buildVariantSelects();
      refreshBarHeight();
    }));
  }

  function collapseMinimal() {
    const productRow = bar.querySelector('.sticky-atc__product-row');
    bar.classList.remove("is--minimal-expanded");
    /* Let both transitions play out, then restore display:none / [hidden] */
    setTimeout(() => {
      if (!bar.classList.contains('is--minimal-expanded')) {
        if (expandable) expandable.hidden = true; // adds [hidden] → display:none → no gap
        if (productRow) productRow.style.display = '';
      }
    }, 340);
    refreshBarHeight();
  }

  let variantSelectClones = [];

  function buildVariantSelects() {
    if (!variantsContainer) return;
    if (variantSelectClones.length > 0) return;

    const variantSelects = section.querySelector(
      "variant-selects, variant-mixed-inputs"
    );
    if (!variantSelects) return;

    /* Only clone the first selector-wrapper to keep the sticky bar clean */
    const wrapper = variantSelects.querySelector(".selector-wrapper");
    if (!wrapper) return;

    const origSelect = wrapper.querySelector("select");
    if (!origSelect) return;

    const wrapperClone = wrapper.cloneNode(true);
    wrapperClone.classList.add("sticky-atc__selector-wrapper");
    const cloneSelect = wrapperClone.querySelector("select");
    cloneSelect.removeAttribute("id");
    cloneSelect.setAttribute("form", formId);
    cloneSelect.classList.add("sticky-atc__select");
    variantsContainer.appendChild(wrapperClone);
    variantSelectClones.push({ orig: origSelect, clone: cloneSelect });

    cloneSelect.addEventListener("change", () => {
      origSelect.value = cloneSelect.value;
      origSelect.dispatchEvent(new Event("change", { bubbles: true }));
    });
    origSelect.addEventListener("change", () => {
      cloneSelect.value = origSelect.value;
    });
  }

  const mobileQuery = window.matchMedia(`(${MOBILE_BREAKPOINT})`);

  async function submitStickyAddToCart() {
    if (!form || submitBtn?.disabled) return;
    const data = new FormData(form);
    const item = Object.fromEntries(data.entries());
    const productFormEl = form.closest("product-form") || form.parentElement;
    const notifyOnAdd = productFormEl?.hasAttribute?.("notify-on-add");
    const redirectOnAdd = productFormEl?.getAttribute?.("redirect-on-add");

    try {
      removeStickyError();
      if (stagedAction) await stagedAction.setState("DOING");
      await Cart.add(item);
      if (stagedAction) await stagedAction.setState("DONE");
      if (redirectOnAdd) {
        window.location.href = redirectOnAdd;
        return;
      }
      if (notifyOnAdd) {
        await Cart.resetVariantsBeingAdded();
        const cart = document.querySelector(".modal--cart, .popup--cart-notification");
        if (cart && !cart.shown) cart.open();
        else if (cart?.interval) {
          cart.interval.reset();
          cart.interval.start();
        }
      } else {
        await Cart.resetLatestAddedProduct();
        await Cart.resetVariantsBeingAdded(item.id);
      }
    } catch (error) {
      if (stagedAction) await stagedAction.setState("IDLE");
      renderStickyError(error.message);
    }
  }

  let stickyErrorTimer = null;

  function removeStickyError() {
    if (stickyErrorTimer) {
      clearTimeout(stickyErrorTimer);
      stickyErrorTimer = null;
    }
    const err = bar.querySelector(".sticky-atc .errors");
    if (err) err.remove();
  }

  function renderStickyError(message) {
    removeStickyError();
    if (submitBtn) {
      const err = document.createElement("div");
      err.classList.add("errors", "qty-error", "u-small");
      err.textContent = message;
      submitBtn.before(err);
      stickyErrorTimer = setTimeout(() => {
        err.remove();
        stickyErrorTimer = null;
      }, 4000);
    }
  }

  function onStickySubmit(e) {
    e.preventDefault();
    if (!form || submitBtn?.disabled) return;

    /* No desktop o seletor fica sempre visível; só no mobile expande no primeiro clique */
    if (
      mobileQuery.matches &&
      hasVariants &&
      submitBtn.hasAttribute("data-action-delay") &&
      !bar.classList.contains("is--minimal-expanded")
    ) {
      expandMinimal();
      return;
    }

    submitStickyAddToCart();
  }

  if (submitBtn) {
    submitBtn.addEventListener("click", onStickySubmit);
  }

  if (closeBtn) {
    closeBtn.addEventListener("click", () => {
      collapseMinimal();
    });
  }

  const variantSelects = section.querySelector("variant-selects, variant-radios, variant-mixed-inputs");
  if (variantSelects) {
    variantSelects.addEventListener("on:variant:change", (e) => {
      if (e.detail?.variant) updateFromVariant(e.detail.variant);
    });
  }

  function isBarEnabledForViewport() {
    return (mobileQuery.matches && showOnMobile) || (!mobileQuery.matches && showOnDesktop);
  }

  function applyVisibility() {
    if (!isBarEnabledForViewport()) {
      hideBar();
      return;
    }
    if (stickyShow === "always") {
      showBar();
    }
  }

  /* Regra: sticky aparece só quando o TOPO da viewport ultrapassou o botão ATC original */
  const originalAtcBtn = form?.querySelector('[name="add"]');

  function isAtcBtnBelowTop() {
    if (!originalAtcBtn) return false;
    return originalAtcBtn.getBoundingClientRect().top < 0;
  }

  applyVisibility();

  if (stickyShow === "when_hidden" && originalAtcBtn) {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (!isBarEnabledForViewport()) {
          hideBar();
          return;
        }
        /* Só mostra se o topo da viewport já passou o botão (bottom < 0),
           evita aparecer no carregamento quando o botão ainda é visível */
        if (entry.isIntersecting) {
          hideBar();
        } else if (isAtcBtnBelowTop()) {
          showBar();
        } else {
          hideBar();
        }
      },
      {
        root: null,
        /* threshold 1 garante que só dispara quando o botão inteiro sai/entra */
        threshold: [0, 1],
      }
    );
    observer.observe(originalAtcBtn);
  }

  /* Quando o .footer__bottom entra na viewport, esconde a barra; quando sai, volta a mostrar se o ATC já passou do topo */
  const footerBottomEl = document.querySelector(".footer__bottom");
  if (footerBottomEl) {
    const footerObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (!isBarEnabledForViewport()) return;
        if (entry.isIntersecting) {
          hideBar();
        } else {
          /* Footer saiu da viewport (scroll para cima): mostrar barra de novo se o botão ATC já ultrapassou o topo */
          if (stickyShow === "when_hidden" && originalAtcBtn && isAtcBtnBelowTop()) {
            showBar();
          }
        }
      },
      { root: null, rootMargin: "0px", threshold: 0 }
    );
    footerObserver.observe(footerBottomEl);
  }

  mobileQuery.addEventListener("change", () => {
    if (!isBarEnabledForViewport()) {
      hideBar();
      return;
    }
    if (stickyShow === "always") {
      showBar();
      return;
    }
    if (isAtcBtnBelowTop()) showBar();
    else hideBar();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initStickyAtc);
} else {
  initStickyAtc();
}
